import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle, AlertCircle, Clock, FileX, Mail, MessageSquareWarning,
  ShieldAlert, GitBranch, Sparkles, ArrowRight, Activity, FileCheck2,
  TrendingUp, Users, Calendar, Bell, Search, Building2, Home, Camera,
  ClipboardList, Eye, Tag, Layers, Handshake, Trophy, ChevronRight,
  Check, FileText, Wallet, ShieldCheck,
} from "lucide-react";

const BRAND = {
  blue: "#4F6BFF",
  indigo: "#3B4BFB",
  purple: "#4338CA",
  gradient: "linear-gradient(135deg, #4F6BFF 0%, #3B4BFB 55%, #4338CA 100%)",
};

/* ─────────────────────────────────────────────────────────
   Shared hooks
   ───────────────────────────────────────────────────────── */
const useInView = (rootMargin = "-12% 0px") => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return undefined;
    const el = ref.current;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { rootMargin, threshold: 0.05 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [rootMargin]);
  return [ref, inView];
};

const easeOut = (t) => 1 - Math.pow(1 - t, 3);

const useTicker = (initial, makeNext, intervalMs) => {
  const [value, setValue] = useState(initial);
  useEffect(() => {
    const id = setInterval(() => setValue((p) => makeNext(p)), intervalMs);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs]);
  return value;
};

const useCountUp = (target, duration = 800) => {
  const [value, setValue] = useState(target);
  const fromRef = useRef(target);
  useEffect(() => {
    const from = fromRef.current;
    if (from === target) return undefined;
    const start = performance.now();
    let raf;
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      setValue(from + (target - from) * easeOut(t));
      if (t < 1) raf = requestAnimationFrame(step);
      else fromRef.current = target;
    };
    raf = requestAnimationFrame(step);
    return () => raf && cancelAnimationFrame(raf);
  }, [target, duration]);
  return value;
};

const fmtGBPCompact = (n) => {
  if (n >= 1_000_000) return `£${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `£${Math.round(n / 1_000)}K`;
  return `£${Math.round(n).toLocaleString()}`;
};

/* ─────────────────────────────────────────────────────────
   Section 1 — INDUSTRY PROBLEM
   Premium operational warning section with floating issue cards.
   ───────────────────────────────────────────────────────── */
const ProblemCard = ({ Icon, label, title, sub, tone = "rose", float = "cf-float", delay = 0, className = "" }) => {
  const tones = {
    rose:   { bg: "rgba(255,241,242,0.85)",  border: "rgba(244,63,94,0.30)",  iconBg: "linear-gradient(135deg,#FB7185,#E11D48)", text: "text-rose-700",  pillBg: "bg-rose-50",  pillBorder: "border-rose-200",  pillText: "text-rose-700"  },
    amber:  { bg: "rgba(255,251,235,0.85)",  border: "rgba(245,158,11,0.30)", iconBg: "linear-gradient(135deg,#FBBF24,#D97706)", text: "text-amber-700", pillBg: "bg-amber-50", pillBorder: "border-amber-200", pillText: "text-amber-700" },
    slate:  { bg: "rgba(241,245,249,0.85)",  border: "rgba(100,116,139,0.30)", iconBg: "linear-gradient(135deg,#94A3B8,#475569)", text: "text-slate-700", pillBg: "bg-slate-50", pillBorder: "border-slate-200", pillText: "text-slate-700" },
  };
  const t = tones[tone];
  return (
    <div
      className={`${className} ${float} rounded-2xl p-4`}
      style={{
        background: t.bg,
        backdropFilter: "saturate(160%) blur(18px)",
        WebkitBackdropFilter: "saturate(160%) blur(18px)",
        border: `1px solid ${t.border}`,
        boxShadow: "0 24px 50px -22px rgba(15,23,42,0.22), 0 0 0 1px rgba(255,255,255,0.55) inset",
        animationDelay: `${delay}ms`,
        minWidth: 240,
      }}
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white shrink-0"
          style={{ background: t.iconBg, boxShadow: "0 8px 18px -8px rgba(15,23,42,0.30)" }}>
          <Icon size={15} strokeWidth={2.4} />
        </div>
        <div className="min-w-0 flex-1">
          <p className={`text-[10px] font-bold uppercase tracking-[0.14em] ${t.text}`}>{label}</p>
          <p className="font-display text-[14px] font-bold text-slate-900 tracking-tight mt-0.5 leading-tight">
            {title}
          </p>
          <p className="text-[11.5px] text-slate-600 mt-1 leading-snug">{sub}</p>
        </div>
      </div>
    </div>
  );
};

export const IndustryProblemSection = () => {
  const [ref, inView] = useInView();
  return (
    <section
      ref={ref}
      className="relative max-w-[1280px] mx-auto px-6 lg:px-10 pb-28 lg:pb-32"
      data-testid="industry-problem"
    >
      {/* Section opener */}
      <div className="text-center mb-14 lg:mb-20">
        <p
          className={`text-[11px] font-bold uppercase tracking-[0.24em] ${inView ? "cf-fade-up" : "opacity-0"}`}
          style={{
            background: BRAND.gradient,
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          The Problem
        </p>
        <h2
          className={`font-display font-bold tracking-[-0.038em] text-slate-900 mt-3 leading-[1.02] ${inView ? "cf-fade-up" : "opacity-0"}`}
          style={{ fontSize: "clamp(34px, 5vw, 60px)", animationDelay: "100ms" }}
        >
          Property transactions break when
          <br />
          <span style={{
            background: "linear-gradient(135deg, #E11D48 0%, #DC2626 60%, #9F1239 100%)",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}>
            communication fragments.
          </span>
        </h2>
        <p
          className={`mt-6 text-[16px] lg:text-[18px] text-slate-600 max-w-[720px] mx-auto leading-relaxed ${inView ? "cf-fade-up" : "opacity-0"}`}
          style={{ animationDelay: "180ms" }}
        >
          One missing AML form. One quiet solicitor. One lost email. That's all it takes for a chain to collapse.
          The average UK transaction loses <span className="font-semibold text-slate-900">38 days</span> to chasing
          and confusion that no one is paid to track.
        </p>
      </div>

      {/* Operational visual: a delayed pipeline with floating issue cards */}
      <div className={`relative ${inView ? "cf-fade-up" : "opacity-0"}`} style={{ animationDelay: "280ms" }}>
        {/* Ambient halo */}
        <div
          aria-hidden="true"
          className="absolute -inset-x-12 -inset-y-10 -z-10 pointer-events-none rounded-[48px]"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(244,63,94,0.10) 0%, rgba(245,158,11,0.06) 35%, transparent 70%)",
            filter: "blur(20px)",
          }}
        />

        {/* Stalled timeline backbone */}
        <div
          className="relative rounded-[24px] bg-white border border-slate-200/80 px-6 lg:px-10 py-10 lg:py-12 overflow-hidden"
          style={{
            boxShadow:
              "0 50px 100px -30px rgba(15,23,42,0.22), 0 12px 30px -10px rgba(15,23,42,0.08)",
          }}
        >
          {/* Header strip */}
          <div className="flex items-center justify-between mb-7">
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full bg-rose-50 border border-rose-200">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-70 cf-pulse" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-rose-500" />
                </span>
                <span className="text-[10.5px] font-bold text-rose-700 uppercase tracking-[0.12em]">Stalled</span>
              </span>
              <p className="font-display text-[14px] font-bold text-slate-900 tracking-tight">
                Day 38 of 90 · 27 Tudor Street, London
              </p>
            </div>
            <p className="hidden md:block text-[11px] font-semibold text-slate-500 tracking-wide">
              Chain health · <span className="text-rose-600 font-bold">42</span>
            </p>
          </div>

          {/* Stage rail */}
          <div className="relative">
            <div className="grid grid-cols-7 gap-1">
              {[
                { label: "Offer",      state: "done"     },
                { label: "AML",        state: "done"     },
                { label: "Memo",       state: "done"     },
                { label: "Mortgage",   state: "blocked"  },
                { label: "Searches",   state: "blocked"  },
                { label: "Enquiries",  state: "upcoming" },
                { label: "Exchange",   state: "upcoming" },
              ].map((s) => (
                <div key={s.label} className="flex flex-col items-center gap-1.5">
                  <span
                    className="w-3 h-3 rounded-full"
                    style={{
                      background:
                        s.state === "done"
                          ? "linear-gradient(135deg,#10B981,#059669)"
                          : s.state === "blocked"
                          ? "linear-gradient(135deg,#FB7185,#E11D48)"
                          : "#E2E8F0",
                      boxShadow:
                        s.state === "blocked"
                          ? "0 0 0 4px rgba(244,63,94,0.18)"
                          : "none",
                    }}
                  />
                  <span className={`text-[10px] font-semibold tracking-wide uppercase ${s.state === "blocked" ? "text-rose-700" : s.state === "done" ? "text-slate-700" : "text-slate-400"}`}>
                    {s.label}
                  </span>
                </div>
              ))}
            </div>
            <div className="absolute left-0 right-0 top-[5px] h-px bg-slate-200 -z-10" />
            {/* Block flare */}
            <span
              aria-hidden="true"
              className="absolute -top-4 left-[calc(45%+0.5rem)] w-[30%] h-12 -z-10 blur-2xl"
              style={{
                background:
                  "radial-gradient(ellipse at center, rgba(244,63,94,0.30), transparent 70%)",
              }}
            />
          </div>

          {/* Floating issue cards layered around the stalled point */}
          <div className="relative mt-10 grid grid-cols-1 lg:grid-cols-3 gap-5">
            <ProblemCard
              Icon={ShieldAlert}
              label="Solicitor delay"
              title="No response · 4 days"
              sub="Buyer-side conveyancer hasn't replied to enquiries. Exchange now slipping."
              tone="rose"
              delay={0}
            />
            <ProblemCard
              Icon={FileX}
              label="AML outstanding"
              title="Buyer ID not verified"
              sub="Compliance documents requested 6 days ago. Manual chase needed today."
              tone="rose"
              float="cf-float-alt"
              delay={300}
            />
            <ProblemCard
              Icon={Mail}
              label="Missing document"
              title="Mortgage offer pending"
              sub="Broker has not forwarded the formal offer letter. Listed as outstanding."
              tone="amber"
              delay={600}
            />
            <ProblemCard
              Icon={MessageSquareWarning}
              label="Communication gap"
              title="Buyer has gone quiet"
              sub="No engagement for 9 days. Confidence eroding across the chain."
              tone="amber"
              float="cf-float-alt"
              delay={900}
            />
            <ProblemCard
              Icon={GitBranch}
              label="Chain bottleneck"
              title="Onward purchase blocked"
              sub="Top-of-chain seller has not instructed solicitor. 1 link from collapse."
              tone="rose"
              delay={1200}
            />
            <ProblemCard
              Icon={Clock}
              label="Lost visibility"
              title="No central tracker"
              sub="Updates scattered across email, WhatsApp, and the agent's notebook."
              tone="slate"
              float="cf-float-alt"
              delay={1500}
            />
          </div>
        </div>

        {/* Transition line */}
        <div className="text-center mt-14 lg:mt-20">
          <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-slate-500 mb-3">
            The Solution
          </p>
          <p
            className="font-display font-bold tracking-[-0.030em] text-slate-900 leading-[1.05]"
            style={{ fontSize: "clamp(24px, 3vw, 36px)" }}
          >
            ChainFlow centralises the entire transaction process
            <br className="hidden md:block" />
            <span className="md:ml-2" style={{
              background: BRAND.gradient,
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}>into one operating system.</span>
          </p>
        </div>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────────────────────
   Section 2 — LIVE TRANSACTION COMMAND CENTRE
   ───────────────────────────────────────────────────────── */

const LIVE_FEED_POOL = [
  { tag: "M", text: "Memorandum of sale issued",    deal: "27 Tudor St" },
  { tag: "A", text: "AML cleared · buyer side",      deal: "10 High St" },
  { tag: "S", text: "Searches returned",              deal: "26 Priory Lane" },
  { tag: "B", text: "Buyer uploaded ID",              deal: "7 Bolton Rd" },
  { tag: "C", text: "Contracts signed",               deal: "41 Station Rd" },
  { tag: "X", text: "Exchange agreed",                deal: "8 Heritage Mews" },
  { tag: "P", text: "Proof of funds verified",        deal: "12 Park Sq" },
  { tag: "T", text: "Solicitor confirmed instruction", deal: "63 Westwood" },
];

const fmtAgo = (msAgo) => {
  if (msAgo < 60_000) return "just now";
  const m = Math.floor(msAgo / 60_000);
  if (m < 60) return `${m}m ago`;
  return `${Math.floor(m / 60)}h ago`;
};

const PanelHeader = ({ title, badge, sub }) => (
  <div className="flex items-start justify-between gap-3 mb-4">
    <div className="min-w-0">
      <p className="font-display text-[14px] font-bold text-slate-900 tracking-tight">{title}</p>
      {sub && <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p>}
    </div>
    {badge}
  </div>
);

const Panel = ({ children, className = "", style = {} }) => (
  <div
    className={`relative rounded-2xl bg-white border border-slate-200/80 p-5 ${className}`}
    style={{
      boxShadow:
        "0 1px 0 rgba(255,255,255,0.7) inset, 0 24px 50px -28px rgba(15,23,42,0.16), 0 4px 14px -8px rgba(79,107,255,0.06)",
      ...style,
    }}
  >
    {children}
  </div>
);

export const CommandCentreSection = () => {
  const [ref, inView] = useInView();

  /* Pipeline live ticker */
  const pipelineTarget = useTicker(
    14_280_000,
    (prev) => {
      const drift = Math.random() * 80_000 - 40_000;
      return prev + drift + (14_280_000 - prev) * 0.10;
    },
    4_000,
  );
  const pipelineDisplay = useCountUp(pipelineTarget, 900);

  /* Active deals ±2 every 6s */
  const activeDeals = useTicker(
    164,
    (prev) => Math.max(155, Math.min(175, prev + (Math.random() < 0.5 ? -2 : 2))),
    6_000,
  );

  /* Fees at risk */
  const feeAtRiskTarget = useTicker(
    148_500,
    (prev) => {
      const drift = Math.random() * 6_000 - 3_500;
      return Math.round((Math.max(120_000, Math.min(170_000, prev + drift))) / 100) * 100;
    },
    7_000,
  );
  const feeAtRiskDisplay = useCountUp(feeAtRiskTarget, 900);

  /* Live feed */
  const baseFeed = useMemo(() => {
    const now = Date.now();
    return [
      { id: 1, tag: "S", text: "Searches returned",        deal: "26 Priory Lane",   at: now - 8 * 60_000 },
      { id: 2, tag: "M", text: "Memo issued",              deal: "27 Tudor St",      at: now - 22 * 60_000 },
      { id: 3, tag: "B", text: "Buyer uploaded ID",        deal: "7 Bolton Rd",      at: now - 65 * 60_000 },
      { id: 4, tag: "A", text: "AML cleared · buyer",      deal: "10 High St",       at: now - 2 * 60 * 60_000 },
      { id: 5, tag: "C", text: "Contracts signed",         deal: "41 Station Rd",    at: now - 4 * 60 * 60_000 },
    ];
  }, []);
  const [feed, setFeed] = useState(baseFeed);
  useEffect(() => {
    const id = setInterval(() => {
      setFeed((prev) => {
        const pick = LIVE_FEED_POOL[Math.floor(Math.random() * LIVE_FEED_POOL.length)];
        const fresh = { id: Date.now() + Math.random(), tag: pick.tag, text: pick.text, deal: pick.deal, at: Date.now() };
        return [fresh, ...prev.slice(0, 4)];
      });
    }, 4_500);
    return () => clearInterval(id);
  }, []);

  /* Exchange countdown */
  const [, force] = useState(0);
  useEffect(() => {
    const id = setInterval(() => force((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const targetExchange = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 3);
    d.setHours(11, 30, 0, 0);
    return d.getTime();
  }, []);
  const msLeft = Math.max(0, targetExchange - Date.now());
  const days = Math.floor(msLeft / 86_400_000);
  const hours = Math.floor((msLeft % 86_400_000) / 3_600_000);
  const mins = Math.floor((msLeft % 3_600_000) / 60_000);
  const secs = Math.floor((msLeft % 60_000) / 1000);

  /* Risk progress bars */
  const risks = [
    { label: "Solicitor inactive · 27 Tudor St",     pct: 78, days: 4, tone: "rose" },
    { label: "Mortgage valuation pending · 10 High", pct: 55, days: 3, tone: "amber" },
    { label: "Searches overdue · 26 Priory",         pct: 65, days: 3, tone: "rose" },
    { label: "Management pack outstanding · 7 Bolton", pct: 45, days: 5, tone: "amber" },
  ];

  /* Doc requests */
  const docRequests = [
    { label: "Proof of Funds",    deal: "27 Tudor St",  state: "uploaded" },
    { label: "AML / ID",          deal: "10 High St",   state: "uploaded" },
    { label: "Mortgage Offer",    deal: "26 Priory",    state: "awaiting" },
    { label: "Contract Pack",     deal: "7 Bolton Rd",  state: "requested" },
  ];

  return (
    <section
      ref={ref}
      className="relative pb-28 lg:pb-36 overflow-hidden"
      data-testid="command-centre"
    >
      {/* Atmospheric backdrop — same brand language as hero */}
      <div aria-hidden="true" className="absolute inset-0 -z-10 pointer-events-none">
        <div
          className="absolute -top-32 left-1/2 -translate-x-1/2 w-[1200px] h-[760px] rounded-full blur-3xl cf-glow-pulse"
          style={{
            background: "radial-gradient(ellipse at center, rgba(67, 56, 202,0.16) 0%, rgba(79,107,255,0.10) 35%, transparent 70%)",
          }}
        />
        <div className="absolute inset-0 cf-hero-grid opacity-60" />
      </div>

      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        {/* Section opener */}
        <div className="text-center mb-14 lg:mb-20">
          <p
            className={`text-[11px] font-bold uppercase tracking-[0.24em] ${inView ? "cf-fade-up" : "opacity-0"}`}
            style={{
              background: BRAND.gradient,
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Flow Centre
          </p>
          <h2
            className={`font-display font-bold tracking-[-0.038em] text-slate-900 mt-3 leading-[1.02] ${inView ? "cf-fade-up" : "opacity-0"}`}
            style={{ fontSize: "clamp(34px, 5.2vw, 64px)", animationDelay: "100ms" }}
          >
            One screen.
            <br />
            <span style={{
              background: BRAND.gradient,
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              WebkitTextFillColor: "transparent",
              filter: "drop-shadow(0 4px 28px rgba(67, 56, 202,0.28))",
            }}>Every transaction in motion.</span>
          </h2>
          <p
            className={`mt-6 text-[16px] lg:text-[18px] text-slate-600 max-w-[720px] mx-auto leading-relaxed ${inView ? "cf-fade-up" : "opacity-0"}`}
            style={{ animationDelay: "180ms" }}
          >
            The Flow Centre is the operational heartbeat of your agency. Live pipeline, live risk,
            live activity—all flowing through a single calm surface designed for the way real chains run.
          </p>
        </div>

        {/* Dashboard composition */}
        <div className={`relative ${inView ? "cf-fade-up" : "opacity-0"}`} style={{ animationDelay: "280ms" }}>
          {/* Browser chrome */}
          <div
            className="hidden md:flex items-center gap-2 h-9 px-4 rounded-t-[24px] -mb-px"
            style={{
              background: "linear-gradient(180deg, rgba(248,250,252,0.95) 0%, rgba(255,255,255,0.95) 100%)",
              border: "1px solid rgba(226,232,240,0.9)",
              borderBottom: "1px solid rgba(226,232,240,0.6)",
            }}
          >
            <span className="w-2.5 h-2.5 rounded-full bg-rose-300/80" />
            <span className="w-2.5 h-2.5 rounded-full bg-amber-300/80" />
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-300/80" />
            <span className="ml-3 text-[10.5px] font-semibold text-slate-500 tracking-wide">
              app.chainflowos.com / command-centre
            </span>
            <span className="ml-auto text-[10.5px] font-semibold text-slate-400 inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 cf-pulse" /> Live
            </span>
          </div>

          {/* Main grid */}
          <div
            className="relative rounded-[24px] md:rounded-t-none md:rounded-b-[24px] bg-white border border-slate-200/80 p-5 lg:p-7 overflow-hidden"
            style={{
              boxShadow:
                "0 60px 120px -30px rgba(15,23,42,0.30), 0 18px 40px -16px rgba(79,107,255,0.10)",
            }}
          >
            {/* Top KPI strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 lg:gap-4 mb-5">
              {/* Pipeline */}
              <Panel className="!p-4 lg:!p-5">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                  Pipeline this month
                </p>
                <p className="font-display text-[24px] lg:text-[28px] font-bold tracking-tight text-slate-900 mt-1.5 tabular-nums leading-none">
                  {fmtGBPCompact(pipelineDisplay)}
                </p>
                <p className="text-[10.5px] text-emerald-600 font-semibold mt-1.5 inline-flex items-center gap-1">
                  <TrendingUp size={11} /> ▲ 22% vs last month
                </p>
              </Panel>

              {/* Active deals */}
              <Panel className="!p-4 lg:!p-5">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                  Active deals
                </p>
                <p className="font-display text-[24px] lg:text-[28px] font-bold tracking-tight text-slate-900 mt-1.5 tabular-nums leading-none">
                  {activeDeals}
                </p>
                <p className="text-[10.5px] text-slate-500 font-semibold mt-1.5">
                  Across <span className="text-slate-900 font-bold">5 branches</span>
                </p>
              </Panel>

              {/* Exchange countdown */}
              <Panel className="!p-4 lg:!p-5">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                  Next exchange
                </p>
                <p className="font-display text-[20px] lg:text-[22px] font-bold tracking-tight text-slate-900 mt-1.5 tabular-nums leading-none">
                  {String(days).padStart(2, "0")}d {String(hours).padStart(2, "0")}h {String(mins).padStart(2, "0")}m {String(secs).padStart(2, "0")}s
                </p>
                <p className="text-[10.5px] text-indigo-600 font-semibold mt-1.5 truncate">
                  27 Tudor St · Buyer ready
                </p>
              </Panel>

              {/* Fees at risk */}
              <Panel className="!p-4 lg:!p-5" style={{
                background: "linear-gradient(180deg, rgba(255,241,242,0.5) 0%, #FFFFFF 100%)",
                borderColor: "rgba(244,63,94,0.20)",
              }}>
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-rose-600">
                  Fees at risk
                </p>
                <p className="font-display text-[24px] lg:text-[28px] font-bold tracking-tight text-rose-700 mt-1.5 tabular-nums leading-none">
                  £{Math.round(feeAtRiskDisplay).toLocaleString()}
                </p>
                <p className="text-[10.5px] text-rose-600 font-semibold mt-1.5 inline-flex items-center gap-1">
                  <AlertCircle size={11} /> 4 deals · review now
                </p>
              </Panel>
            </div>

            {/* Three-column body */}
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)_minmax(0,1fr)] gap-3 lg:gap-4">
              {/* LEFT: Risk alerts */}
              <Panel>
                <PanelHeader
                  title="Risk alerts"
                  sub="Sorted by severity"
                  badge={
                    <span className="inline-flex items-center gap-1 h-6 px-2 rounded-md bg-rose-50 border border-rose-200 text-[10px] font-bold text-rose-700">
                      <AlertCircle size={10} /> 4
                    </span>
                  }
                />
                <ul className="space-y-3.5">
                  {risks.map((r, i) => (
                    <li key={i}>
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <p className="text-[11.5px] font-semibold text-slate-800 truncate flex-1">
                          {r.label}
                        </p>
                        <span className={`text-[10px] font-bold tabular-nums ${r.tone === "rose" ? "text-rose-600" : "text-amber-700"}`}>
                          {r.days}d
                        </span>
                      </div>
                      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${r.pct}%`,
                            background:
                              r.tone === "rose"
                                ? "linear-gradient(90deg,#FB7185,#E11D48)"
                                : "linear-gradient(90deg,#FBBF24,#D97706)",
                            transition: "width 1.2s ease-out",
                          }}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              </Panel>

              {/* MIDDLE: Live activity feed */}
              <Panel>
                <PanelHeader
                  title="Live activity"
                  sub="All branches · all deals"
                  badge={
                    <span className="inline-flex items-center gap-1 h-6 px-2 rounded-md bg-emerald-50 border border-emerald-200">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 cf-pulse" />
                      <span className="text-[10px] font-bold text-emerald-700">Live</span>
                    </span>
                  }
                />
                <ul className="space-y-2.5">
                  {feed.map((a, i) => (
                    <li
                      key={a.id}
                      className={`flex items-center gap-2.5 px-2 py-1.5 -mx-2 rounded-lg ${i === 0 ? "cf-feed-slide bg-indigo-50/40" : ""}`}
                    >
                      <span
                        className="w-7 h-7 rounded-lg text-white text-[10px] font-bold flex items-center justify-center shrink-0"
                        style={{ background: BRAND.gradient }}
                      >
                        {a.tag}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-[11.5px] font-semibold text-slate-900 truncate">
                          {a.text}
                        </p>
                        <p className="text-[10px] text-slate-500 truncate">{a.deal}</p>
                      </div>
                      <span className="text-[10px] text-slate-400 tabular-nums shrink-0">
                        {fmtAgo(Date.now() - a.at)}
                      </span>
                    </li>
                  ))}
                </ul>
              </Panel>

              {/* RIGHT: Document requests + team activity */}
              <div className="space-y-3 lg:space-y-4">
                <Panel>
                  <PanelHeader
                    title="Document tracker"
                    sub="Requested · Awaiting · Uploaded"
                    badge={
                      <span className="inline-flex items-center gap-1 h-6 px-2 rounded-md bg-indigo-50 border border-indigo-200 text-[10px] font-bold text-indigo-700">
                        <FileText size={10} /> 4
                      </span>
                    }
                  />
                  <ul className="space-y-2">
                    {docRequests.map((d, i) => (
                      <li key={i} className="flex items-center gap-2.5">
                        <span
                          className={`w-2 h-2 rounded-full ${
                            d.state === "uploaded"
                              ? "bg-emerald-500"
                              : d.state === "awaiting"
                              ? "bg-amber-500"
                              : "bg-slate-300"
                          }`}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] font-semibold text-slate-900 truncate">{d.label}</p>
                          <p className="text-[10px] text-slate-500 truncate">{d.deal}</p>
                        </div>
                        <span
                          className={`text-[9.5px] font-bold uppercase tracking-wide ${
                            d.state === "uploaded"
                              ? "text-emerald-700"
                              : d.state === "awaiting"
                              ? "text-amber-700"
                              : "text-slate-500"
                          }`}
                        >
                          {d.state}
                        </span>
                      </li>
                    ))}
                  </ul>
                </Panel>

                <Panel>
                  <PanelHeader
                    title="Team activity"
                    sub="Last 60 min"
                    badge={
                      <span className="inline-flex items-center -space-x-1.5">
                        {["JC", "EW", "DR"].map((i, k) => (
                          <span
                            key={k}
                            className="w-5 h-5 rounded-full text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-white"
                            style={{
                              background: `linear-gradient(135deg, hsl(${(k * 47) % 360}, 70%, 55%), hsl(${(k * 47 + 60) % 360}, 70%, 45%))`,
                            }}
                          >
                            {i}
                          </span>
                        ))}
                      </span>
                    }
                  />
                  <ul className="space-y-1.5 text-[11px]">
                    <li className="flex items-center justify-between"><span className="text-slate-700">James reassigned 3 tasks</span><span className="text-slate-400 tabular-nums">2m</span></li>
                    <li className="flex items-center justify-between"><span className="text-slate-700">Eleanor uploaded contract pack</span><span className="text-slate-400 tabular-nums">8m</span></li>
                    <li className="flex items-center justify-between"><span className="text-slate-700">David chased solicitor</span><span className="text-slate-400 tabular-nums">14m</span></li>
                    <li className="flex items-center justify-between"><span className="text-slate-700">Priya marked AML cleared</span><span className="text-slate-400 tabular-nums">31m</span></li>
                  </ul>
                </Panel>
              </div>
            </div>

            {/* Bottom: Chain health + progressing deals */}
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-3 lg:gap-4 mt-3 lg:mt-4">
              <Panel>
                <PanelHeader
                  title="Progressing now"
                  sub="6 deals advancing this hour"
                  badge={
                    <span className="inline-flex items-center gap-1 h-6 px-2 rounded-md bg-indigo-50 border border-indigo-200 text-[10px] font-bold text-indigo-700">
                      <Activity size={10} /> 6
                    </span>
                  }
                />
                <ul className="space-y-2.5">
                  {[
                    { addr: "27 Tudor St · London EC4",     stage: "Searches",   pct: 60, tone: "indigo" },
                    { addr: "10 High St · Stratford",        stage: "Mortgage",   pct: 42, tone: "indigo" },
                    { addr: "26 Priory Lane · Hampstead",    stage: "Enquiries",  pct: 71, tone: "indigo" },
                    { addr: "8 Heritage Mews · Mayfair",     stage: "Exchange",   pct: 92, tone: "emerald" },
                  ].map((d, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <p className="text-[11.5px] font-semibold text-slate-900 truncate">{d.addr}</p>
                          <span className="text-[10px] font-bold text-slate-500 tabular-nums">{d.pct}%</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${d.pct}%`,
                              background:
                                d.tone === "emerald"
                                  ? "linear-gradient(90deg,#10B981,#059669)"
                                  : BRAND.gradient,
                              transition: "width 1.2s ease-out",
                            }}
                          />
                        </div>
                      </div>
                      <span className="text-[9.5px] font-bold uppercase tracking-wide text-slate-500 w-[78px] text-right">
                        {d.stage}
                      </span>
                    </li>
                  ))}
                </ul>
              </Panel>

              <Panel className="overflow-hidden" style={{
                background: "linear-gradient(180deg, rgba(245,243,255,0.5) 0%, #FFFFFF 100%)",
              }}>
                <PanelHeader
                  title="Operational notifications"
                  badge={
                    <span className="inline-flex items-center gap-1 h-6 px-2 rounded-md bg-indigo-50 border border-indigo-200 text-[10px] font-bold text-indigo-700">
                      <Bell size={10} /> 3
                    </span>
                  }
                />
                <ul className="space-y-2.5 text-[11.5px]">
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-rose-500 mt-1.5 cf-pulse" />
                    <p className="text-slate-700">
                      <span className="font-bold text-slate-900">Solicitor escalation:</span> 27 Tudor — auto-chase sent to Stephen Holt.
                    </p>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5" />
                    <p className="text-slate-700">
                      <span className="font-bold text-slate-900">Mortgage delay:</span> Lloyds valuation rebooked for Friday.
                    </p>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5" />
                    <p className="text-slate-700">
                      <span className="font-bold text-slate-900">Exchange ready:</span> 8 Heritage Mews on track for Friday.
                    </p>
                  </li>
                </ul>
              </Panel>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────────────────────
   Section 4 — HOW CHAINFLOW WORKS · 8-step transaction flow
   ───────────────────────────────────────────────────────── */
export const HowItWorksSection = () => {
  const [ref, inView] = useInView();
  const [active, setActive] = useState(4); // Default to "Deal Room" highlight

  // Auto-advance the highlighted step every 2.6s for live feel
  useEffect(() => {
    if (!inView) return undefined;
    const id = setInterval(() => {
      setActive((s) => (s + 1) % 8);
    }, 2600);
    return () => clearInterval(id);
  }, [inView]);

  const steps = [
    { Icon: ClipboardList, label: "Instruction",    sub: "Seller signs · OTM" },
    { Icon: Camera,        label: "Launch Hub",     sub: "Photos · EPC · Listing" },
    { Icon: Eye,           label: "Viewings",       sub: "Booked · feedback" },
    { Icon: Tag,           label: "Offers",         sub: "Scored · negotiated" },
    { Icon: Layers,        label: "Deal Room",      sub: "Participants · live" },
    { Icon: GitBranch,     label: "Sales Progression", sub: "12 stages tracked" },
    { Icon: Handshake,     label: "Exchange",       sub: "Contracts · funds" },
    { Icon: Trophy,        label: "Completion",     sub: "Keys · invoiced" },
  ];

  return (
    <section
      ref={ref}
      className="relative max-w-[1280px] mx-auto px-6 lg:px-10 pb-28 lg:pb-32"
      data-testid="how-it-works"
    >
      {/* Section opener */}
      <div className="text-center mb-14 lg:mb-20">
        <p
          className={`text-[11px] font-bold uppercase tracking-[0.24em] ${inView ? "cf-fade-up" : "opacity-0"}`}
          style={{
            background: BRAND.gradient,
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          How it Works
        </p>
        <h2
          className={`font-display font-bold tracking-[-0.038em] text-slate-900 mt-3 leading-[1.02] ${inView ? "cf-fade-up" : "opacity-0"}`}
          style={{ fontSize: "clamp(34px, 5vw, 60px)", animationDelay: "100ms" }}
        >
          From instruction to keys.
          <br />
          <span style={{
            background: BRAND.gradient,
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}>One continuous flow.</span>
        </h2>
        <p
          className={`mt-6 text-[16px] lg:text-[18px] text-slate-600 max-w-[680px] mx-auto leading-relaxed ${inView ? "cf-fade-up" : "opacity-0"}`}
          style={{ animationDelay: "180ms" }}
        >
          Every transaction passes through the same eight stages. ChainFlow connects them
          into one operational thread—visible to every stakeholder at every step.
        </p>
      </div>

      {/* Flow */}
      <div className={`relative ${inView ? "cf-fade-up" : "opacity-0"}`} style={{ animationDelay: "260ms" }}>
        {/* Connecting rail */}
        <div
          aria-hidden="true"
          className="hidden lg:block absolute top-[58px] left-10 right-10 h-[2px] -z-10"
          style={{
            background:
              "linear-gradient(90deg, rgba(99,102,241,0.35) 0%, rgba(79,107,255,0.55) 50%, rgba(67, 56, 202,0.35) 100%)",
            boxShadow: "0 0 12px rgba(79,107,255,0.30)",
          }}
        />
        {/* Animated active dot moving along */}
        <div
          aria-hidden="true"
          className="hidden lg:block absolute top-[54px] z-10"
          style={{
            left: `calc(${(active / 7) * 100}% - 5px)`,
            transition: "left 700ms cubic-bezier(0.22,1,0.36,1)",
          }}
        >
          <span className="block w-2.5 h-2.5 rounded-full"
            style={{
              background: BRAND.gradient,
              boxShadow: "0 0 0 4px rgba(79,107,255,0.20), 0 4px 14px rgba(79,107,255,0.55)",
            }}
          />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4 lg:gap-2">
          {steps.map((s, i) => {
            const isActive = i === active;
            const isPast = i < active;
            return (
              <div key={s.label} className="relative flex flex-col items-center text-center">
                {/* Step number above icon */}
                <span className="text-[10px] font-bold tabular-nums text-slate-400 mb-1.5">
                  {String(i + 1).padStart(2, "0")}
                </span>
                {/* Icon node */}
                <button
                  type="button"
                  onClick={() => setActive(i)}
                  className="relative w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-500"
                  style={{
                    background: isActive
                      ? BRAND.gradient
                      : isPast
                      ? "linear-gradient(135deg, rgba(99,102,241,0.10), rgba(67, 56, 202,0.10))"
                      : "#FFFFFF",
                    border: isActive
                      ? "1px solid rgba(79,107,255,0.4)"
                      : isPast
                      ? "1px solid rgba(79,107,255,0.30)"
                      : "1px solid rgba(226,232,240,0.9)",
                    boxShadow: isActive
                      ? "0 16px 40px -10px rgba(67, 56, 202,0.55), inset 0 1px 0 rgba(255,255,255,0.30)"
                      : "0 8px 20px -10px rgba(15,23,42,0.10)",
                    transform: isActive ? "translateY(-2px) scale(1.04)" : "none",
                  }}
                >
                  <s.Icon
                    size={20}
                    strokeWidth={2.2}
                    className={isActive ? "text-white" : isPast ? "text-indigo-700" : "text-slate-500"}
                  />
                  {isActive && (
                    <span
                      aria-hidden="true"
                      className="absolute inset-0 rounded-2xl cf-pulse"
                      style={{
                        boxShadow: "0 0 0 0 rgba(79,107,255,0.40)",
                        animation: "cfStepPulse 1.8s ease-out infinite",
                      }}
                    />
                  )}
                </button>
                {/* Label */}
                <p className={`mt-3 font-display text-[12.5px] font-bold tracking-tight transition-colors ${isActive ? "text-slate-900" : "text-slate-700"}`}>
                  {s.label}
                </p>
                <p className="text-[10.5px] text-slate-500 mt-0.5 leading-snug">{s.sub}</p>

                {/* Mobile connector arrow */}
                {i < steps.length - 1 && (
                  <ChevronRight
                    size={14}
                    className="hidden sm:block lg:hidden absolute -right-2 top-12 text-slate-300"
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Active step detail card */}
        <div
          className="mt-12 lg:mt-16 rounded-[20px] bg-white border border-slate-200/80 p-6 lg:p-8 max-w-[820px] mx-auto"
          style={{
            boxShadow:
              "0 1px 0 rgba(255,255,255,0.7) inset, 0 24px 60px -28px rgba(15,23,42,0.18)",
            transition: "all 500ms cubic-bezier(0.22,1,0.36,1)",
          }}
        >
          <div className="flex items-start gap-5">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center text-white shrink-0"
              style={{
                background: BRAND.gradient,
                boxShadow: "0 16px 36px -12px rgba(67, 56, 202,0.50)",
              }}
            >
              {React.createElement(steps[active].Icon, { size: 22, strokeWidth: 2.2 })}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-slate-500">
                Stage {String(active + 1).padStart(2, "0")} of 08
              </p>
              <p className="font-display text-[22px] lg:text-[24px] font-bold text-slate-900 tracking-tight mt-0.5 leading-tight">
                {[
                  "Instruction · Seller signs the OTM agreement, ChainFlow auto-creates the deal record.",
                  "Launch Hub · Branded onboarding for the seller — photography, EPC, solicitor instruction.",
                  "Viewings · Bookings, attendance, qualitative feedback, follow-up automation.",
                  "Offers · Auto-scored against funding, chain, and engagement signals before negotiation.",
                  "Deal Room · Live workspace for property, participants, progression, and communication.",
                  "Sales Progression · 12 canonical stages auto-advanced from real evidence.",
                  "Exchange · Contracts signed, deposit funds verified, exchange formally completed.",
                  "Completion · Keys handed over, agency invoice raised, deal archived with full audit trail.",
                ][active]}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CSS for step pulse */}
      <style>{`
        @keyframes cfStepPulse {
          0%   { box-shadow: 0 0 0 0 rgba(79,107,255,0.45); }
          70%  { box-shadow: 0 0 0 12px rgba(79,107,255,0); }
          100% { box-shadow: 0 0 0 0 rgba(79,107,255,0); }
        }
      `}</style>
    </section>
  );
};
